import axios from 'axios';
import * as ol from 'openlayers';
import * as QueryString from 'qs';
import * as React from "react";
import { RouteComponentProps } from 'react-router';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import GeolocationService from '../../../api/geolocation_service';
import BusPositionService from '../../../api/stadtbus/bus_position_service';
import BusStopService from '../../../api/stadtbus/bus_stop_service';
import { encodeTripId } from '../../../helpers';
import { BusPositionSource, IBusPosition } from '../../../interfaces/stadtbus/bus_position';
import { IPosition } from '../../../interfaces/stadtbus/bus_stop';
import { lazyInject } from '../../../inversify';
import ObserverComponent from '../../observer_component';
import "./stop_map.css";

interface IProps {
  height?: string,
  lat?: string,
  lng?: string,
  zoom?: string
}

interface IStopMapProps extends Partial<RouteComponentProps<IProps>> {
  fullscreen?: boolean;
}

interface IStopMapState {
  displayUnknownLines: boolean;
  hideError: boolean;
  linienSollAnzahl: any;
  linienIstAnzahl: any;
  popupContent: JSX.Element;
}

interface IBusPositionFeature {
  busPosition: IBusPosition;
  feature: ol.Feature;
}

class StopMap extends React.Component<IStopMapProps, IStopMapState> {
  @lazyInject(BusPositionService)
  private readonly busPositionService: BusPositionService;
  @lazyInject(GeolocationService)
  private readonly geolocationService: GeolocationService;
  @lazyInject(BusStopService)
  private readonly busStopService: BusStopService;

  // emits an event if the component is unmounted
  private readonly onUnmount: Subject<boolean> = new Subject<boolean>();

  private onFollowBus: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  // My position marker
  private readonly markerVector = new ol.source.Vector({});
  private readonly myRef: React.RefObject<any>;
  private myMarkerFeature: ol.Feature;

  // Line data
  private linienNumber: number[] = [881, 882, 883, 884, 885];
  private linien: string[] = ["881", "882", "883", "884", "885"];
  private linieColors = { 881: [2, 107, 173, 1], 882: [220, 1, 9, 1], 883: [238, 238, 0, 1], 884: [131, 192, 9, 1], 885: [156, 156, 156, 1] };
  private zoomLevels: number[] = [14, 16, 17];
  private geoJSONs: any[][] = [[null, null, null, null, null], [null, null, null, null, null], [null, null, null, null, null]];
  private geoJSONFeaturePosition: { [id: number]: number } = {};
  private currentZoomLevel: number = -1;
  private loadedCount: number = 0;

  // Map objects
  private styles: any;
  private map: ol.Map;
  private overlay: ol.Overlay;
  private busPositionFeatures: IBusPositionFeature[] = [];

  // Map startup settings
  private startLat: number = 52.0267;
  private startLng: number = 8.9007;
  private startZoom: number = 13;
  private startHeight: string = "500px";

  /**
   * Loading props
   * @param props Stop map props
   */
  constructor(props: IStopMapProps) {
    super(props);

    if (this.props.match && this.props.match.params) {
      if (this.props.match.params.lat && this.props.match.params.lng) {
        this.startLat = Number.parseFloat(this.props.match.params.lat);
        this.startLng = Number.parseFloat(this.props.match.params.lng);
        this.startZoom = 17;
      }
      if (this.props.match.params.zoom) {
        this.startZoom = Number.parseInt(this.props.match.params.zoom!);
      }
      if (this.props.match.params.height) {
        this.startHeight = this.props.match.params.height!;
      }
    }

    const query = this.props.location && QueryString.parse(this.props.location.search, {ignoreQueryPrefix: true});

    this.state = {
      displayUnknownLines: query && query.all_busses === 'true' || false,
      hideError: query && query.hide_error === 'true' || false,
      linienIstAnzahl: {},
      // TODO: Get from timetable data
      linienSollAnzahl: {881: 2, 882: 2, 883: 2, 884: 1, 885: 1},
      popupContent: <div/>,
    } as IStopMapState;

    this.myRef = React.createRef();
  }

  public componentDidMount() {
    const query = this.props.location && QueryString.parse(this.props.location.search, {ignoreQueryPrefix: true});

    // Setup stop map
    this.loadMap();
    this.loadGeoJSON();

    // Update GeoJSON by Zoom
    this.map.on('moveend', () => {
      if (this.loadedCount >= 15) {
        this.updateGeoJSON();
      }
    });

    if (!query || query.disable_location !== 'true') {
      // My position marker
      this.geolocationService.watchPosition()
        .pipe(takeUntil(this.onUnmount))
        .subscribe((position: IPosition) => {
          this.myMarkerFeature.setGeometry(new ol.geom.Point(ol.proj.fromLonLat([position.longitude, position.latitude])));
        });
    }

    const busPositionBehavior = new BehaviorSubject<IBusPosition[]>([]);

    this.busPositionService.onBusPositions(BusPositionSource.gps)
      .pipe(
        takeUntil(this.onUnmount),
        map(positions => this.state.displayUnknownLines ? positions : positions.filter(p => p.line)),
      ).subscribe(busPositionBehavior);

    // Subscribe to the bus positions
    busPositionBehavior
      .pipe(takeUntil(this.onUnmount))
      .subscribe(this.updateBusPositions.bind(this));

    // Update the popup according to the bus positions
    this.onFollowBus
      .pipe(
        switchMap(id =>
          busPositionBehavior
            .pipe(
              map(positions => positions.find(p => p.id === id)),
            )),
        takeUntil(this.onUnmount))
      .subscribe(p => {
        this.overlay.setPosition(p && ol.proj.fromLonLat([p.position.longitude, p.position.latitude]));

        if (p) {
          this.setState({
            popupContent: (
              <div id="popup-content">
                <table style={{width: '100%'}}>
                  <tbody>
                  <tr>
                    <th>Bus ID</th>
                    <td>{p.id}</td>
                  </tr>
                  <tr>
                    <th>Linie</th>
                    <td>{p.line}</td>
                  </tr>
                  {p.lineDestination && (
                    <tr>
                      <th>Richtung</th>
                      <td>{p.lineDestination}</td>
                    </tr>
                  )}
                  <tr>
                    <th>Quelle</th>
                    <td>{p.source}</td>
                  </tr>
                  {p.delay && (
                    <tr>
                      <th>Verspätung</th>
                      <td>{p.delay} Minute{p!.delay && p.delay > 1 && 'n'}</td>
                    </tr>
                  ) || undefined}
                  {p.nextStopId && (
                    <tr>
                      <th>Nächster Halt:</th>
                      <td>
                        <ObserverComponent>{this.busStopService.onBusStop(p.nextStopId).pipe(map(s => s.name))}</ObserverComponent>
                      </td>
                    </tr>
                  )}
                  {p.journeyRef !== undefined && p.operationDate !== undefined && (
                    <tr>
                      <td colSpan={2}>
                        <button className="mapButton" onClick={() => this.handlePopupButtonClick(p)}>Fahrt anzeigen
                        </button>
                      </td>
                    </tr>
                  )}
                  </tbody>
                </table>
              </div>
            ),
          });
        }
      });
  }

  public componentWillUnmount() {
    this.onUnmount.next(true);
  }

  public render() {
    const fehlendeLinien = {};

    Object.keys(this.state.linienSollAnzahl)
      .forEach(linie => {
        let fehlend = this.state.linienSollAnzahl[linie];

        if (this.state.linienIstAnzahl[linie] !== undefined) {
          fehlend -= this.state.linienIstAnzahl[linie];
        }

        if (fehlend > 0) {
          fehlendeLinien[linie] = fehlend;
        }
      });

    let missingGPSIndicator;
    const fehlendeLinienKeys = Object.keys(fehlendeLinien);
    if (!this.state.hideError && fehlendeLinienKeys.length > 0) {
      const text = `Keine GPS-Daten für die Linie${fehlendeLinienKeys.length > 1 ? 'n' : ''}: ${fehlendeLinienKeys.join(', ')}`;
      missingGPSIndicator = <div className="missingGPSIndicator">
        {text}
      </div>;
    }

    return (
      <div>
        {missingGPSIndicator}
        <div id="map" className="map" ref={this.myRef} style={{
          height: !this.props.fullscreen ? this.startHeight : '100vh',
          marginBottom: !this.props.fullscreen ? '16px' : 0,
          width: "100%",
        }}/>
        <div className="arrow_box" id="popup-container">
          {this.state.popupContent}
        </div>
      </div>
    );
  }

  private handlePopupButtonClick(position: IBusPosition) {
    if (this.props.history && position.journeyRef && position.operationDate) {
      this.props.history.push(`${process.env.PUBLIC_URL}/stadtbus/linie/${encodeTripId(position.journeyRef, position.operationDate)}`);
    }
  }

  private updateBusPositions(positions: IBusPosition[]) {
    // remove features that don't exist anymore
    const removedFeatures = this.busPositionFeatures.filter(f => positions.find(p => p.id === f.busPosition.id) === undefined);
    this.busPositionFeatures = this.busPositionFeatures.filter(f => removedFeatures.find(bpf => bpf.busPosition.id === f.busPosition.id) === undefined);
    removedFeatures.forEach(f => this.markerVector.removeFeature(f.feature));

    // Add or update positions
    positions.forEach((p: IBusPosition) => {
      // Search for existing bus position feature
      const busPositionFeature = this.busPositionFeatures.find(f => f.busPosition.id === p.id);
      if (busPositionFeature) {
        const shouldUpdate = busPositionFeature.busPosition.position.latitude !== p.position.latitude
          || busPositionFeature.busPosition.position.longitude !== p.position.longitude
          || busPositionFeature.feature.get('type') !== p.line;

        if (shouldUpdate) {
          busPositionFeature.busPosition = p;
          const position = ol.proj.fromLonLat([p.position.longitude, p.position.latitude]);
          busPositionFeature.feature.setGeometry(new ol.geom.Point(position));
          busPositionFeature.feature.set('type', p.line);
        }
      } else {
        const newFeature = new ol.Feature({
          geometry: new ol.geom.Point(ol.proj.fromLonLat([p.position.longitude, p.position.latitude])),
          type: p.line,
        });
        this.busPositionFeatures.push({
          busPosition: p,
          feature: newFeature
        });
        this.markerVector.addFeature(newFeature);
      }
    });

    const linienIstAnzahl: {[key: string]: number} = {};

    this.busPositionFeatures.forEach(f => {
      if (f.busPosition.line) {
        if (linienIstAnzahl[f.busPosition.line] === undefined) {
          linienIstAnzahl[f.busPosition.line] = 1;
        } else {
          linienIstAnzahl[f.busPosition.line]++;
        }
      }
    });

    // Only change the state if the Ist-Anzahl really changed
    const oldKeys = Object.keys(this.state.linienIstAnzahl);
    const newKeys = Object.keys(linienIstAnzahl);
    if (oldKeys.length !== newKeys.length || oldKeys.find(k => oldKeys[k] !== newKeys[k])) {
      this.setState({
        linienIstAnzahl
      });
    }
  }

  /**
   * Show bus information popup
   */
  private showPopup(e: ol.MapBrowserEvent) {
    // don't act if the button in the popup was clicked.
    if (!e.originalEvent.target || !e.originalEvent.srcElement || e.originalEvent.srcElement.tagName !== 'BUTTON') {
      let busPositionFeature: IBusPositionFeature | undefined;
      this.map.forEachFeatureAtPixel(e.pixel, (feature: ol.Feature) => {
        const foundPosition = this.busPositionFeatures.find(bpf => bpf.feature === feature);
        if (foundPosition) {
          busPositionFeature = foundPosition;
        }
      });

      this.onFollowBus.next(busPositionFeature ? busPositionFeature.busPosition.id : null);
    }
  }

  private loadMap() {
    this.styles = {};

    // Bus Icons
    for (const linie of this.linien) {
      this.styles[linie] = new ol.style.Style({
        image: new ol.style.Icon({
          scale: 0.1,
          src: '/stadtbus-images/bus_' + linie + '.png',
        })
      });
    }

    // Me Marker
    this.styles[0] = new ol.style.Style({
      image: new ol.style.Icon({
        scale: 1,
        src: '/marker.png',
      })
    });

    // Default Marker
    const defaultStyle = new ol.style.Style({
      image: new ol.style.Circle({
        fill: new ol.style.Fill({
          color: 'green',
        }),
        radius: 5,
      }),
    });

    // My marker feature
    this.myMarkerFeature = new ol.Feature({
      geometry: new ol.geom.Point(ol.proj.fromLonLat([0, 0])),
      type: 0
    });
    this.markerVector.addFeature(this.myMarkerFeature);

    // Overlay
    this.overlay = new ol.Overlay({
      autoPan: true,
      element: document.getElementById('popup-container')!,
      offset: [0, -10],
      positioning: 'bottom-center',
      stopEvent: false,
    });

    // Layers
    const markerLayer = new ol.layer.Vector({
      source: this.markerVector,
      style: (feature) => {
        return this.styles[feature.get('type')] || defaultStyle;
      }
    });

    /*
      krz Source (TileWMS schient auf iOS noch nicht zu funktionieren, Prüfbedarf)
      source: new ol.source.TileWMS(
        {
          params: {layers: "osm_de"},
          projection: "EPSG:3857",
          url: "http://map.krz.de/mapproxy/osm_nrw/service",
        }
      )
    */

    // Map
    this.map = new ol.Map({
      layers: [
        new ol.layer.Tile({
          source: new ol.source.OSM()
        }),
        markerLayer
      ],
      view: new ol.View({
        center: ol.proj.fromLonLat([this.startLng, this.startLat]),
        zoom: this.startZoom
      })
    });
    this.map.addOverlay(this.overlay);

    // Show popup on click
    this.map.on('click', (e: any) => this.showPopup(e));

    this.map.setTarget(this.myRef.current);
  }

  private loadGeoJSON() {
    const linienPromises = [];

    // Load line geojsons
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < this.linien.length; j++) {
        linienPromises.push(axios.get("/geodata/" + this.linien[j] + '.' + (i + 1) + '.geojson').then((geojsonObject: any) => {
          this.geoJSONs[i][j] = geojsonObject;
          this.loadedCount++;
          if (this.loadedCount >= 15) {
            this.updateGeoJSON();
          }
        }));
      }
    }
  }

  private updateGeoJSON() {
    // Load line geojsons
    let level = 2;
    level = (this.map.getView().getZoom() <= this.zoomLevels[1]) ? 1 : level;
    level = (this.map.getView().getZoom() <= this.zoomLevels[0]) ? 0 : level;

    if (level !== this.currentZoomLevel) {
      for (let i = 0; i < this.linien.length; i++) {
        this.showGeoJSONFromObject(this.geoJSONs[level][this.linienNumber[i] - 881], this.linien[i]);
      }
    }

    this.currentZoomLevel = level;
  }

  private showGeoJSONFromObject(geojsonObject: any, linie: string) {
    // Bus stop image
    const pointImage = new ol.style.Circle({
      fill: new ol.style.Fill({
        color: 'black'
      }),
      radius: 3,
    });

    // LineString color
    const styles = {
      'LineString': new ol.style.Style({
        stroke: new ol.style.Stroke({
          color: this.linieColors[linie],
          width: 3
        })
      }),
      // MultiLineString color
      'MultiLineString': new ol.style.Style({
        stroke: new ol.style.Stroke({
          color: this.linieColors[linie],
          width: 3
        }),
      }),
      // Point style
      'Point': new ol.style.Style({
        image: pointImage
      }),
    };

    // Style mapping
    const styleFunction = (feature: any) => {
      return styles[feature.getGeometry().getType()];
    };

    // Feature in vector
    const vectorSource = new ol.source.Vector({
      features: (new ol.format.GeoJSON()).readFeatures(geojsonObject.data, {
        dataProjection: "EPSG:4326",
        featureProjection: 'EPSG:3857'
      })
    });

    // Remove old layer
    this.map.getLayers().remove(this.geoJSONFeaturePosition[linie]);
    // Apply new vector to layer
    const vector = new ol.layer.Vector({
      source: vectorSource,
      style: styleFunction
    });
    this.geoJSONFeaturePosition[linie] = vector;
    this.map.getLayers().insertAt(1, vector);
  }
}

export default StopMap;


// WEBPACK FOOTER //
// ./src/components/stadtbus/stop_map/stop_map.tsx